import React, { useEffect, useRef, useState } from 'react';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import Icon, { IconTypes } from "@shared/ui/atoms/icon/Icon";
import Colours from '@shared/ui/atoms/Colours';
import Layout from '@shared/ui/organisms/layout/Layout';
import { isInTeams } from "@shared/utils/TeamsUtils";
import WhereaboutsHeader from "./WhereaboutsHeader";
import WhereaboutsSidebar from "./WhereaboutsSidebar";
import { MonthlyWhereaboutsView } from "@features/wall-planner/pages/MonthlyWhereaboutsView";
import { WhereaboutsViews } from "../team-movements/components/WhereaboutsViews";
import { DailyWhereaboutsView } from "./DailyWhereaboutsView";
import { WeeklyCalendarView } from "./WeeklyCalendarView";
import { WeekNotesPanel } from "./WeekNotesPanel";
import {
  MainWhereaboutsContent,
  whereaboutsViewportHeight,
} from "@features/whereabouts/pages/whereabouts-v2/Structure";
import { V2Colours } from "./WhereaboutsV2Styles";
import { tablet } from "@shared/ui/atoms/MediaQueries";
import { PageView, ViewMode } from "./types";
import {
  selectGlobalHeaderCollapsed,
  selectIsMonthView,
  selectNotesPanelOpen,
  selectPageView,
  selectSidebarOpen,
  selectViewMode,
  setGlobalHeaderCollapsed,
  setNotesPanelOpen,
  toggleNotesPanel,
  toggleSidebar,
} from "@features/whereabouts/store/whereaboutsView.duck";
import { WhereaboutsScope } from '@features/whereabouts/models/whereaboutsScope';
import { WhereaboutsBackLink, WhereaboutsScopeProvider } from './WhereaboutsScopeContext';

interface Props {
  // Which people the page shows. Omit for the signed-in user's followed
  // groups (/whereabouts/team); pass a team or workgroup scope to pin the
  // whole page (all view modes, header, sidebar, notes) to that one group.
  scope?: WhereaboutsScope;
  backLink?: WhereaboutsBackLink;
}

export default function WhereaboutsPageV2({ scope, backLink }: Props) {
  const dispatch = useDispatch();
  const viewMode = useSelector(selectViewMode);
  const pageView = useSelector(selectPageView);
  const sidebarOpen = useSelector(selectSidebarOpen);
  const notesPanelOpen = useSelector(selectNotesPanelOpen);
  const isMonthView = useSelector(selectIsMonthView);
  const globalHeaderCollapsed = useSelector(selectGlobalHeaderCollapsed);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(setGlobalHeaderCollapsed(false));
    };
  }, [dispatch]);

  const onContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const top = el.scrollTop;
    const roomBelow = el.scrollHeight - el.clientHeight - top;

    if (globalHeaderCollapsed) {
      if (top < 16) dispatch(setGlobalHeaderCollapsed(false));
    } else if (top > 48 && roomBelow > 120) {
      dispatch(setGlobalHeaderCollapsed(true));
    }
    setShowScrollTop(top > 200);
  };

  const scrollToTop = () => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <WhereaboutsScopeProvider scope={scope} backLink={backLink}>
      <Layout hideFooter={true} fullScreenView={true} style={{maxWidth: '100%'}}>
        <PageContainer $isInTeams={isInTeams()} $headerCollapsed={globalHeaderCollapsed}>
          <MainContent>
            <WhereaboutsHeader showMobileSidebarButton={true} />
            <Content ref={contentRef} $sidebarOpen={sidebarOpen} $notesPanelOpen={notesPanelOpen} onScroll={onContentScroll}>
              {isMonthView && <MonthViewContainer><MonthlyWhereaboutsView hideDateFlicker={true} /></MonthViewContainer>}
              {pageView === PageView.Calendar && viewMode === ViewMode.Week && <WeeklyCalendarView />}
              {pageView === PageView.Grid && viewMode === ViewMode.Week && <GridViewContainer><WhereaboutsViews hideDateFlicker={true} hideDayHeaders={true} /></GridViewContainer>}
              {viewMode === ViewMode.Day && <GridViewContainer><DailyWhereaboutsView /></GridViewContainer>}
            </Content>
          </MainContent>
          {notesPanelOpen && <WeekNotesPanel sidebarOpen={sidebarOpen} onClose={() => dispatch(setNotesPanelOpen(false))} />}
          <WhereaboutsSidebar isOpen={sidebarOpen} onWeekNotesClick={() => dispatch(toggleNotesPanel())} onToggle={() => dispatch(toggleSidebar())} />
          <ScrollTopButton $visible={showScrollTop} $sidebarOpen={sidebarOpen} onClick={scrollToTop} aria-label="Scroll to top">
            <Icon icon={IconTypes.Up} />
          </ScrollTopButton>
        </PageContainer>
      </Layout>
    </WhereaboutsScopeProvider>
  );
}

const PageContainer = styled.div<{ $isInTeams: boolean; $headerCollapsed: boolean }>`
  max-height: inherit;
  position: relative;
  display: flex;
  width: 100%;
  margin: 0 auto;
  height: ${props => whereaboutsViewportHeight(props.$isInTeams, props.$headerCollapsed)};
  background: ${Colours.veryLightGrey};
  overflow: hidden;
  transition: height 0.2s ease;
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Content = styled(MainWhereaboutsContent)`
  flex: 1;
  overflow-y: auto;
`;

const GridViewContainer = styled.div`
  max-width: 1400px;
  width: 100%;
  padding: 0 16px;
  @media (${tablet}) {
    padding: 0;
  }
`;

const MonthViewContainer = styled.div`
  padding-left: 4px;
`;

const ScrollTopButton = styled.button<{ $visible: boolean; $sidebarOpen: boolean }>`
  position: absolute;
  bottom: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${V2Colours.navy};
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1101;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  transform: ${props => props.$visible ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.2s ease, transform 0.2s ease, right 0.25s ease;
  .icon {
    color: white;
    font-size: 18px;
  }
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.22);
  }
  @media (${tablet}) {
    right: ${props => props.$sidebarOpen ? '384px' : '104px'};
  }
`;
